<!-- from claude -->
<script>
  // Props
  let { 
    accept = "*", 
    multiple = false, 
    maxSize = null,
    onUpload = null,
    onDone = null
  } = $props();

  // State
  let selectedFile = $state(null);
  let isDragging = $state(false);
  let isUploading = $state(false);
  let error = $state(null);
  
  let fileInput = $state();

  // Handle file selection from input
  function handleFileSelect(event) {
    const files = event.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }

  // Handle drag and drop
  function handleDragOver(event) {
    event.preventDefault();
    isDragging = true;
  }

  function handleDragLeave(event) {
    event.preventDefault();
    isDragging = false;
  }

  function handleDrop(event) {
    event.preventDefault();
    isDragging = false;
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }

  // Process selected file
  function processFile(file) {
    error = null;
    
    // Validate file size if maxSize is specified
    if (maxSize && file.size > maxSize) {
      error = `File size exceeds ${formatBytes(maxSize)} limit`;
      return;
    }
    
    selectedFile = file;
  }

  // Upload handler
  async function handleUpload() {
    if (!selectedFile || !onUpload || !onDone) return;
    
    isUploading = true;
    error = null;
    
    try {
      await onUpload(selectedFile);
      // Optionally clear the selected file after successful upload
      selectedFile = null;
      fileInput.value = '';
      onDone();
    } catch (err) {
      error = err.message || 'Upload failed';
    } finally {
      isUploading = false;
    }
  }

  // Clear selection
  function clearSelection() {
    selectedFile = null;
    error = null;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // Utility function to format file size
  function formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
</script>

<div class="file-upload-container">
  <!-- Drag and Drop Area -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div 
    class="drop-zone" 
    class:dragging={isDragging}
    class:has-file={selectedFile}
    ondragover={handleDragOver}
    ondragleave={handleDragLeave}
    ondrop={handleDrop}
    onclick={() => fileInput?.click()}
  >
    {#if selectedFile}
      <div class="file-info">
        <div class="file-icon">📄</div>
        <div class="file-details">
          <div class="file-name">{selectedFile.name}</div>
          {#if selectedFile.type}
            <div class="file-name">type: {selectedFile.type}</div>
          {:else}
            <div>type unknown</div>
          {/if}
          <div class="file-size">{formatBytes(selectedFile.size)}</div>
        </div>
        <button 
          class="clear-btn" 
          onclick={(e) => { e.stopPropagation(); clearSelection(); }}
        >
          ✕
        </button>
      </div>
    {:else}
      <div class="drop-prompt">
        <div class="upload-icon">📁</div>
        <p>Click to select a file or drag and drop</p>
        {#if accept !== "*"}
          <p class="accept-hint">Accepts: {accept}</p>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Hidden file input -->
  <input
    bind:this={fileInput}
    type="file"
    {accept}
    {multiple}
    onchange={handleFileSelect}
    style="display: none;"
  />

  <!-- Error message -->
  {#if error}
    <div class="error-message">
      {error}
    </div>
  {/if}

  <!-- Upload button -->
  {#if selectedFile && onUpload && onDone}
    <button 
      class="upload-btn" 
      onclick={handleUpload}
      disabled={isUploading}
    >
      {isUploading ? 'Uploading...' : 'Upload File'}
    </button>
  {/if}
</div>

<style>
  .file-upload-container {
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
  }

  .drop-zone {
    border: 2px dashed #ccc;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background: #fafafa;
  }

  .drop-zone:hover {
    border-color: #007bff;
    background: #f0f8ff;
  }

  .drop-zone.dragging {
    border-color: #007bff;
    background: #e6f3ff;
    transform: scale(1.02);
  }

  .drop-zone.has-file {
    border-color: #28a745;
    background: #f8fff8;
  }

  .file-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    text-align: left;
  }

  .file-icon {
    font-size: 2rem;
  }

  .file-details {
    flex: 1;
  }

  .file-name {
    font-weight: 600;
    margin-bottom: 0.25rem;
    word-break: break-word;
  }

  .file-size {
    color: #666;
    font-size: 0.875rem;
  }

  .clear-btn {
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
  }

  .clear-btn:hover {
    background: #c82333;
  }

  .drop-prompt {
    color: #666;
  }

  .upload-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .accept-hint {
    font-size: 0.875rem;
    color: #999;
    margin-top: 0.5rem;
  }

  .error-message {
    background: #f8d7da;
    color: #721c24;
    padding: 0.75rem;
    border-radius: 4px;
    margin-top: 1rem;
    border: 1px solid #f5c6cb;
  }

  .upload-btn {
    background: #007bff;
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
    margin-top: 1rem;
    width: 100%;
    transition: background-color 0.2s;
  }

  .upload-btn:hover:not(:disabled) {
    background: #0056b3;
  }

  .upload-btn:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
</style>

